/**
 * Site-wide constants for SEO and sharing
 * Use REACT_APP_SITE_URL for production canonical URLs (e.g. https://pack-my-cart.com)
 */
export const SITE_URL =
  process.env.REACT_APP_SITE_URL?.trim() ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://pack-my-cart.com');

export const SITE_NAME = 'Pack My Cart';

/** Product-like object for share URL building */
interface ProductLike {
  seo_name?: string | null;
  id: string;
  display_name?: string | null;
}

/**
 * Converts text to an SEO-friendly URL slug (e.g. "Organic Milk" -> "organic-milk").
 */
export function slugify(text: string | null | undefined): string {
  if (!text || typeof text !== 'string') return '';
  return text
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Returns the SEO-friendly slug for a product.
 * Prefers seo_name, then slug from display_name, then id (avoid product ID in share URLs).
 */
export function getProductShareSlug(product: ProductLike | null | undefined): string {
  if (!product) return '';
  if (product.seo_name) return product.seo_name;
  const fromDisplay = slugify(product.display_name);
  if (fromDisplay) return fromDisplay;
  return product.id || '';
}

/**
 * Builds the full share URL for a product (standalone /product/:slug format).
 */
export function getProductShareUrl(product: ProductLike | null | undefined): string {
  const slug = getProductShareSlug(product);
  return slug ? `${SITE_URL}/product/${slug}` : SITE_URL;
}

/**
 * Builds the full share URL for a product in supermarket context.
 */
export function getProductShareUrlInSupermarket(product: ProductLike | null | undefined, supermarketId: string | null | undefined): string {
  const slug = getProductShareSlug(product);
  return slug && supermarketId
    ? `${SITE_URL}/supermarkets/${supermarketId}/products/${slug}`
    : supermarketId
      ? `${SITE_URL}/supermarkets/${supermarketId}/products`
      : SITE_URL;
}
